import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    addDoc,
    query,
    where,
    orderBy,
    limit,
    serverTimestamp,
    increment,
    runTransaction,
    arrayUnion
} from 'firebase/firestore';
import { auth, db, COLLECTIONS, TRANSACTION_TYPES, STATUS } from '../config/firebase';

// ==================== PRODUCTS ====================

// Get All Products
export const getProducts = async () => {
    try {
        const productsRef = collection(db, COLLECTIONS.PRODUCTS);
        const q = query(productsRef, where('isActive', '==', true), orderBy('price', 'asc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting products:', error);
        throw error;
    }
};

// Get User's Purchased Products
export const getUserProducts = async () => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        const userProductsRef = collection(db, COLLECTIONS.USER_PRODUCTS);
        const q = query(
            userProductsRef,
            where('userId', '==', user.uid),
            orderBy('purchaseDate', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting user products:', error);
        throw error;
    }
};

// Purchase Product
export const purchaseProduct = async (product) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        return await runTransaction(db, async (transaction) => {
            const userRef = doc(db, COLLECTIONS.USERS, user.uid);
            const userDoc = await transaction.get(userRef);

            if (!userDoc.exists()) {
                throw new Error('User not found');
            }

            const userData = userDoc.data();
            if (userData.rechargeBalance < product.price) {
                throw new Error('Insufficient balance');
            }

            // Deduct balance
            transaction.update(userRef, {
                rechargeBalance: increment(-product.price),
                updatedAt: serverTimestamp(),
            });

            // Create user product
            const purchaseDate = new Date();
            const expiryDate = new Date(purchaseDate);
            expiryDate.setDate(expiryDate.getDate() + product.validity);

            const userProductRef = doc(collection(db, COLLECTIONS.USER_PRODUCTS));
            transaction.set(userProductRef, {
                userId: user.uid,
                productId: product.id,
                productName: product.name,
                price: product.price,
                dailyIncome: product.dailyIncome,
                validity: product.validity,
                purchaseDate: purchaseDate,
                expiryDate: expiryDate,
                lastClaimDate: null,
                totalClaimed: 0,
                createdAt: serverTimestamp(),
            });

            // Create transaction record
            const transactionRef = doc(collection(db, COLLECTIONS.TRANSACTIONS));
            transaction.set(transactionRef, {
                userId: user.uid,
                type: TRANSACTION_TYPES.PURCHASE,
                amount: -product.price,
                description: `Purchased ${product.name}`,
                status: STATUS.COMPLETED,
                createdAt: serverTimestamp(),
            });


            return userProductRef.id;
        });
    } catch (error) {
        console.error('Error purchasing product:', error);
        throw error;
    }
};

// Claim Daily Income
export const claimDailyIncome = async (userProduct) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        return await runTransaction(db, async (transaction) => {
            const userRef = doc(db, COLLECTIONS.USERS, user.uid);
            const userProductRef = doc(db, COLLECTIONS.USER_PRODUCTS, userProduct.id);

            // 1. ALL READS FIRST
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists()) throw new Error('User not found');
            const userData = userDoc.data();

            const referrers = [];
            let currentId = userData.referredByUid;

            // Fetch up to 3 levels of referrers sequentially before any writes
            for (let i = 0; i < 3; i++) {
                if (!currentId) break;
                const rRef = doc(db, COLLECTIONS.USERS, currentId);
                const rDoc = await transaction.get(rRef);
                if (rDoc.exists()) {
                    referrers.push({ id: currentId, ref: rRef, data: rDoc.data() });
                    currentId = rDoc.data().referredByUid;
                } else {
                    break;
                }
            }

            // 2. ALL WRITES AFTER READS
            // Update user balance
            transaction.update(userRef, {
                withdrawBalance: increment(userProduct.dailyIncome),
                updatedAt: serverTimestamp(),
            });

            // Update user product
            transaction.update(userProductRef, {
                lastClaimDate: new Date(),
                totalClaimed: increment(userProduct.dailyIncome),
            });

            // Create transaction record for the user
            transaction.set(doc(collection(db, COLLECTIONS.TRANSACTIONS)), {
                userId: user.uid,
                type: TRANSACTION_TYPES.DAILY_INCOME,
                amount: userProduct.dailyIncome,
                description: `Daily income from ${userProduct.productName}`,
                status: STATUS.COMPLETED,
                createdAt: serverTimestamp(),
            });

            // Distribute Commissions
            const rates = [0.09, 0.04, 0.02];
            const levels = ['A', 'B', 'C'];

            referrers.forEach((referrer, i) => {
                const commissionAmount = userProduct.dailyIncome * rates[i];
                if (commissionAmount > 0) {
                    transaction.update(referrer.ref, {
                        withdrawBalance: increment(commissionAmount),
                        totalTeamEarnings: increment(commissionAmount),
                        updatedAt: serverTimestamp(),
                    });

                    transaction.set(doc(collection(db, COLLECTIONS.TRANSACTIONS)), {
                        userId: referrer.id,
                        type: TRANSACTION_TYPES.REFERRAL_BONUS,
                        amount: commissionAmount,
                        description: `Level ${levels[i]} commission from ${userData.displayName || 'Team Member'} daily earnings`,
                        status: STATUS.COMPLETED,
                        senderId: user.uid,
                        senderName: userData.displayName || 'Team Member',
                        createdAt: serverTimestamp(),
                    });
                }
            });

            return userProduct.dailyIncome;
        });
    } catch (error) {
        console.error('Error claiming daily income:', error);
        throw error;
    }
};

// ==================== TRANSACTIONS ====================

// Get User Transactions
export const getTransactions = async (limitCount = 50) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        const transactionsRef = collection(db, COLLECTIONS.TRANSACTIONS);
        const q = query(
            transactionsRef,
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc'),
            limit(limitCount)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting transactions:', error);
        throw error;
    }
};

// ==================== RECHARGE ====================

// Get Active Payment Channels
export const getActivePaymentChannels = async () => {
    try {
        const channelsRef = collection(db, COLLECTIONS.PAYMENT_CHANNELS);
        const q = query(channelsRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting payment channels:', error);
        throw error;
    }
};

// Create Recharge Request
export const createRechargeRequest = async (amount, utrNumber, proofBase64) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        const rechargeRef = await addDoc(collection(db, COLLECTIONS.RECHARGE_REQUESTS), {
            userId: user.uid,
            amount,
            utrNumber,
            proofBase64,
            status: STATUS.PENDING,
            createdAt: serverTimestamp(),
        });

        // Create transaction record for recharge (Under Review until admin approves)
        await addDoc(collection(db, COLLECTIONS.TRANSACTIONS), {
            userId: user.uid,
            type: TRANSACTION_TYPES.RECHARGE,
            amount: amount,
            description: 'Recharge request',
            status: STATUS.PENDING,
            rechargeRequestId: rechargeRef.id,
            createdAt: serverTimestamp(),
        });

        return rechargeRef.id;
    } catch (error) {
        console.error('Error creating recharge request:', error);
        throw error;
    }
};

// ==================== WITHDRAWAL ====================

// Create Withdrawal Request
export const createWithdrawalRequest = async (amount, bankId, fundPassword) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        // Check if user already withdrew today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const withdrawalsQuery = query(
            collection(db, COLLECTIONS.WITHDRAWAL_REQUESTS),
            where('userId', '==', user.uid),
            where('createdAt', '>=', today),
            where('createdAt', '<', tomorrow)
        );
        const withdrawalsSnap = await getDocs(withdrawalsQuery);
        if (!withdrawalsSnap.empty) {
            throw new Error('You can only make one withdrawal per day. Please try again tomorrow.');
        }

        return await runTransaction(db, async (transaction) => {
            const userRef = doc(db, COLLECTIONS.USERS, user.uid);
            const userDoc = await transaction.get(userRef);

            if (!userDoc.exists()) {
                throw new Error('User not found');
            }

            const userData = userDoc.data();

            // Verify fund password
            if (userData.fundPassword !== fundPassword) {
                throw new Error('Incorrect fund password');
            }

            // Check balance
            if (userData.withdrawBalance < amount) {
                throw new Error('Insufficient balance');
            }

            // Deduct balance
            transaction.update(userRef, {
                withdrawBalance: increment(-amount),
                updatedAt: serverTimestamp(),
            });

            // Calculate net amount after 6% tax
            const taxAmount = (amount * 6) / 100;
            const netAmount = amount - taxAmount;

            // Create withdrawal request
            const withdrawalRef = doc(collection(db, COLLECTIONS.WITHDRAWAL_REQUESTS));
            transaction.set(withdrawalRef, {
                userId: user.uid,
                amount, // Gross amount (what user requested)
                netAmount, // Net amount (after 6% tax deduction)
                taxAmount, // Tax amount (6%)
                bankId,
                status: STATUS.PENDING,
                createdAt: serverTimestamp(),
            });

            // Create transaction record with NET amount
            const transactionRef = doc(collection(db, COLLECTIONS.TRANSACTIONS));
            transaction.set(transactionRef, {
                userId: user.uid,
                type: TRANSACTION_TYPES.WITHDRAW,
                amount: -netAmount, // Show net amount in transaction history
                grossAmount: amount, // Store gross amount for reference
                taxAmount, // Store tax amount
                description: `Withdrawal request`,
                status: STATUS.PENDING,
                createdAt: serverTimestamp(),
            });

            return withdrawalRef.id;
        });
    } catch (error) {
        console.error('Error creating withdrawal request:', error);
        throw error;
    }
};

// ==================== BANK DETAILS ====================

// Get Bank Details
export const getBankDetails = async () => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        const bankRef = collection(db, COLLECTIONS.BANK_DETAILS);
        const q = query(bankRef, where('userId', '==', user.uid), limit(1));
        const snapshot = await getDocs(q);

        if (snapshot.empty) return null;
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
        console.error('Error getting bank details:', error);
        throw error;
    }
};

// Save Bank Details
export const saveBankDetails = async (accountHolderName, accountNumber, ifscCode, bankName) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        const existingBank = await getBankDetails();

        const bankData = {
            userId: user.uid,
            accountHolderName,
            accountNumber,
            ifscCode,
            bankName,
            updatedAt: serverTimestamp(),
        };

        if (existingBank) {
            await updateDoc(doc(db, COLLECTIONS.BANK_DETAILS, existingBank.id), bankData);
        } else {
            await addDoc(collection(db, COLLECTIONS.BANK_DETAILS), {
                ...bankData,
                createdAt: serverTimestamp(),
            });
        }
    } catch (error) {
        console.error('Error saving bank details:', error);
        throw error;
    }
};

// ==================== GIFT CODES ====================

// Redeem Gift Code
export const redeemGiftCode = async (code) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        return await runTransaction(db, async (transaction) => {
            const giftCodesRef = collection(db, COLLECTIONS.GIFT_CODES);
            const q = query(giftCodesRef, where('code', '==', code.toUpperCase()), limit(1));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                throw new Error('Invalid gift code');
            }

            const giftCodeDoc = snapshot.docs[0];
            const giftCode = giftCodeDoc.data();

            if (!giftCode.isActive && (giftCode.usedCount || 0) >= (giftCode.maxUses || 1)) {
                throw new Error('This gift code is no longer active');
            }

            if ((giftCode.usedCount || 0) >= (giftCode.maxUses || 1)) {
                throw new Error('This gift code has reached its maximum usage limit');
            }

            if (giftCode.usedBy && Array.isArray(giftCode.usedBy) && giftCode.usedBy.includes(user.uid)) {
                throw new Error('You have already redeemed this gift code');
            }

            // Mark code as used by this user
            const newUsedCount = (giftCode.usedCount || 0) + 1;
            const updates = {
                usedBy: arrayUnion(user.uid),
                usedCount: increment(1),
            };

            if (newUsedCount >= (giftCode.maxUses || 1)) {
                updates.isActive = false;
            }

            transaction.update(doc(db, COLLECTIONS.GIFT_CODES, giftCodeDoc.id), updates);

            // Add balance to user
            const userRef = doc(db, COLLECTIONS.USERS, user.uid);
            transaction.update(userRef, {
                withdrawBalance: increment(giftCode.amount),
                updatedAt: serverTimestamp(),
            });

            // Create transaction record
            const transactionRef = doc(collection(db, COLLECTIONS.TRANSACTIONS));
            transaction.set(transactionRef, {
                userId: user.uid,
                type: TRANSACTION_TYPES.GIFT_CODE,
                amount: giftCode.amount,
                description: `Gift code redeemed: ${code}`,
                status: STATUS.COMPLETED,
                createdAt: serverTimestamp(),
            });

            return giftCode.amount;
        });
    } catch (error) {
        console.error('Error redeeming gift code:', error);
        throw error;
    }
};

// ==================== ANNOUNCEMENTS ====================

// Get Active Announcements
export const getActiveAnnouncements = async () => {
    try {
        const announcementsRef = collection(db, COLLECTIONS.ANNOUNCEMENTS);
        const q = query(
            announcementsRef,
            where('isActive', '==', true),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Error getting announcements:', error);
        throw error;
    }
};

// ==================== TEAM / REFERRALS ====================

// Get Team Data
export const getTeamData = async (userUid, referralCode) => {
    try {
        const usersRef = collection(db, COLLECTIONS.USERS);

        // Level 1 - Direct referrals (Support both UID and Code for legacy compatibility)
        const qUid = query(usersRef, where('referredByUid', '==', userUid));
        const qCode = query(usersRef, where('referredBy', '==', referralCode?.toUpperCase()));

        const [snapUid, snapCode] = await Promise.all([getDocs(qUid), getDocs(qCode)]);

        const level1Map = new Map();
        snapUid.docs.forEach(doc => level1Map.set(doc.id, { uid: doc.id, ...doc.data() }));
        snapCode.docs.forEach(doc => level1Map.set(doc.id, { uid: doc.id, ...doc.data() }));

        const level1 = Array.from(level1Map.values());

        // Level 2 - Referrals of referrals (based on referredByUid)
        const level1Uids = level1.map(u => u.uid).filter(Boolean);
        let level2 = [];
        if (level1Uids.length > 0) {
            const batches = [];
            for (let i = 0; i < level1Uids.length; i += 10) {
                const batch = level1Uids.slice(i, i + 10);
                if (batch.length > 0) {
                    const level2Query = query(usersRef, where('referredByUid', 'in', batch));
                    batches.push(getDocs(level2Query));
                }
            }
            const results = await Promise.all(batches);
            level2 = results.flatMap(snapshot =>
                snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }))
            );
        }

        // Level 3 - Referrals of Level 2 (based on referredByUid)
        const level2Uids = level2.map(u => u.uid).filter(Boolean);
        let level3 = [];
        if (level2Uids.length > 0) {
            const batches = [];
            for (let i = 0; i < level2Uids.length; i += 10) {
                const batch = level2Uids.slice(i, i + 10);
                if (batch.length > 0) {
                    const level3Query = query(usersRef, where('referredByUid', 'in', batch));
                    batches.push(getDocs(level3Query));
                }
            }
            const results = await Promise.all(batches);
            level3 = results.flatMap(snapshot =>
                snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }))
            );
        }

        return { level1, level2, level3 };
    } catch (error) {
        console.error('Error getting team data:', error);
        throw error;
    }
};

// Update User Profile
export const updateUserProfile = async (data) => {
    try {
        const user = auth.currentUser;
        if (!user) throw new Error('No authenticated user');

        const userRef = doc(db, COLLECTIONS.USERS, user.uid);
        await updateDoc(userRef, {
            ...data,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating user profile:', error);
        throw error;
    }
};

// ==================== SUPPORT SETTINGS ====================

// Get Support Settings
export const getSupportSettings = async () => {
    try {
        const settingsRef = doc(db, 'settings', 'support');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
            return settingsDoc.data();
        }

        // Return default values if no settings exist
        return {
            whatsappNumber: '+919876543210',
            telegramUsername: 'nextron_support',
            supportEmail: 'support@nextron.com',
            supportHours: 'Mon-Sat: 10AM - 7PM IST',
        };
    } catch (error) {
        console.error('Error getting support settings:', error);
        // Return default values on error
        return {
            whatsappNumber: '+919876543210',
            telegramUsername: 'nextron_support',
            supportEmail: 'support@nextron.com',
            supportHours: 'Mon-Sat: 10AM - 7PM IST',
        };
    }
};

export default {
    getProducts,
    getUserProducts,
    purchaseProduct,
    claimDailyIncome,
    getTransactions,
    getActivePaymentChannels,
    createRechargeRequest,
    createWithdrawalRequest,
    getBankDetails,
    saveBankDetails,
    redeemGiftCode,
    getActiveAnnouncements,
    getTeamData,
    getSupportSettings,
};
